import TranslateIcon from "@mui/icons-material/Translate";
import { Box } from "@mui/material";
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Switch,
  message,
} from "antd";

import { FormInstance } from "antd/lib";
import SpinFC from "antd/lib/spin";
import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UploadImage from "../../components/common/UploadImage";
import TranslationModal from "../../components/common/TranslationModal";
import CommonMultiSelect, { SelectOption } from "../../components/common/CommonMultiSelect";
import { useBreadcrumbLabel } from "../../components/breadcrumb/BreadcrumbContext";
import CategoryChildren from "./CategoryChildren";
import api from "../../hooks/api";
import { useCategoryDetailWithTranslations } from "../../hooks/useCategory";
import { useCountries } from "../../hooks/useCountries";
import { useLanguages } from "../../hooks/useLanguages";
import { pathAPI } from "../../utils/pathApi";
import { fileToBlob } from "../../utils/utils";

const CategoryDetail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { id } = useParams<{ id: string }>();
  const [{ category, translations, isLoading }, { mutate }] =
    useCategoryDetailWithTranslations(id || "");

  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  // Translation editing states
  const [isTranslationModalVisible, setIsTranslationModalVisible] =
    useState(false);

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };
  const [{ data: languages, isLoading: loadingLanguages }] = useLanguages();
  const [{ data: countries }] = useCountries();
  const formRef = useRef<FormInstance>(null);
  const currentLang = localStorage.getItem("language") || "en";
  const currentTranslation = useMemo(() => {
    return (
      translations.find((translation) => translation.language_code === currentLang) ||
      translations[0]
    );
  }, [translations, currentLang]);

  useBreadcrumbLabel(currentTranslation?.translation_name);

  const [serviceImage, setServiceImage] = useState<File | null>(null);
  const [background, setBackground] = useState<File | null>(null);
  const [homeImage, setHomeImage] = useState<File | null>(null);
  const [selectedCountries, setSelectedCountries] = useState<Array<string | number>>(["all"]);
  const [countryError, setCountryError] = useState(false);

  // Map countries to dropdown options format
  const countryOptions: SelectOption[] = useMemo(() => {
    if (!countries) return [];
    return [
      { label: t("category.add.all"), value: "all" },
      ...countries.map((country) => ({
        label: country.name || country.code || "",
        value: country.id,
      })),
    ];
  }, [countries, t]);

  const allCountryIds = useMemo(
    () => (countries || []).map((country) => country.id),
    [countries]
  );

  // When "All" is the only value (loaded from the server), expand it to tick every country
  useEffect(() => {
    if (
      allCountryIds.length > 0 &&
      selectedCountries.length === 1 &&
      selectedCountries[0] === "all"
    ) {
      setSelectedCountries(["all", ...allCountryIds]);
    }
  }, [allCountryIds, selectedCountries]);

  const handleCountryChange = (values: Array<string | number>) => {
    const hadAll = selectedCountries.includes("all");
    const hasAll = values.includes("all");
    const items = values.filter((v) => v !== "all");

    let next: Array<string | number>;
    if (hasAll && !hadAll) {
      // ticked "All" -> tick everything
      next = ["all", ...allCountryIds];
    } else if (!hasAll && hadAll && items.length === allCountryIds.length) {
      // unticked "All" while everything was ticked -> untick everything
      next = [];
    } else if (allCountryIds.length > 0 && items.length === allCountryIds.length) {
      // ticked the last remaining item -> tick "All" too
      next = ["all", ...items];
    } else {
      // plain item toggle; "All" drops out automatically
      next = items;
    }

    if (next.length > 0) {
      setCountryError(false);
    }
    setSelectedCountries(next);
  };

  const putCategory = (data: any) => {
    return api.PUT(`${pathAPI.categoryDetail}/${id}`, data, {}, true);
  };

  const onFinish = async (values: any) => {
    try {
      // Validate that at least one country is selected
      if (!selectedCountries || selectedCountries.length === 0) {
        setCountryError(true);
        message.error(t("category.validation.country_required"));
        return;
      }

      // Clear error if validation passes
      setCountryError(false);

      setLoadingSave(true);

      const formData = new FormData();
      if (serviceImage) {
        const blob = await fileToBlob(serviceImage);
        formData.append(
          "serviceImage",
          blob as Blob,
          serviceImage.name || "default-filename.png"
        );
      }
      if (background) {
        const blob = await fileToBlob(background);
        formData.append(
          "backgroundImage",
          blob as Blob,
          background.name || "default-filename.png"
        );
      }
      if (homeImage) {
        const blob = await fileToBlob(homeImage);
        formData.append(
          "homeImage",
          blob as Blob,
          homeImage.name || "default-filename.png"
        );
      }

      // Add name field for updating the category name
      if (currentTranslation) {
        formData.append("name", currentTranslation.translation_name || "");
      }

      formData.append("active", `${values?.active ? 1 : 0}`);
      formData.append("showHomeImage", `${values?.showHomeImage ? 1 : 0}`);
      formData.append("color", values?.color || "#000");

      // Add countryIds: if "all" is selected, send "0", otherwise send the selected country IDs
      if (selectedCountries.includes("all")) {
        formData.append("countryIds", "0");
      } else {
        selectedCountries.forEach((countryId) => {
          formData.append("countryIds", countryId.toString());
        });
      }

      setLoadingEdit(true);
      await putCategory(formData);
      message.success(t("category.messages.update_success"));

      // Ensure we reload data before continuing
      await mutate();
    } catch (error) {
      console.error("Error updating category:", error);
      message.error(t("category.messages.error"));
    } finally {
      setLoadingEdit(false);
      setLoadingSave(false);
    }
  };

  useEffect(() => {
    if (category) {
      // Initialize form values
      const initialValues = {
        color: category.color || "#000",
        active: category.active ?? false,
        showHomeImage: category.show_home_image ?? true,
        serviceImageUrl: category.service_image || category.active_image || "",
        backgroundUrl: category.background_image || "",
      };
      formRef.current?.setFieldsValue(initialValues);

      // Initialize selected countries from national field
      if (
        category.national &&
        Array.isArray(category.national) &&
        category.national.length > 0 &&
        !category.national.includes(0)
      ) {
        setSelectedCountries(category.national);
      } else {
        setSelectedCountries(["all"]);
      }
    }
  }, [category, isLoading]);

  // Function to open translation modal
  const handleOpenTranslationModal = () => {
    if (!languages || !translations) return;
    setIsTranslationModalVisible(true);
  };

  // Function to handle translation success
  const handleTranslationSuccess = async () => {
    await mutate(); // Refresh data
  };

  if (isLoading || loadingLanguages) {
    return (
      <Box textAlign={"center"}>
        <SpinFC size="large" />
      </Box>
    );
  }

  const settingRow = (label: string, name: string) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      px={2}
      py={1.5}
      sx={{ border: "1px solid var(--app-border-light)", borderRadius: "8px" }}
    >
      <span style={{ fontWeight: 500 }}>{label}</span>
      <Form.Item name={name} valuePropName="checked" style={{ margin: 0 }}>
        <Switch
          checkedChildren={t("category.switch.on")}
          unCheckedChildren={t("category.switch.off")}
        />
      </Form.Item>
    </Box>
  );

  const imageField = (
    name: string,
    label: string,
    url: string,
    onChange: (file: File | null) => void
  ) => (
    <Col xl={8} lg={12} md={12} sm={24} xs={24}>
      <Form.Item
        name={name}
        label={label}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        labelAlign="left"
      >
        <UploadImage
          width={"100%"}
          urlImage={url}
          height={"200px"}
          onChangeFile={onChange}
        />
      </Form.Item>
    </Col>
  );

  return (
    <Box>
      <Form ref={formRef} onFinish={onFinish} {...layout}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card title={t("category.sections.basic_information")} style={{ height: "100%" }}>
              <div className="ant-form-item-label" style={{ marginBottom: "8px" }}>
                <label className="ant-form-item-required">{t("category.fields.name")}</label>
              </div>
              <div
                style={{
                  padding: "8px 12px",
                  background: "var(--app-bg)",
                  border: "1px solid var(--app-border)",
                  borderRadius: "6px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 16,
                }}
              >
                <span>
                  {currentTranslation ? currentTranslation.translation_name : t("category.detail.no_name")}
                </span>
                <Button
                  type="primary"
                  size="small"
                  icon={<TranslateIcon style={{ fontSize: "16px" }} />}
                  onClick={handleOpenTranslationModal}
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  {t("category.manage_translations")}
                </Button>
              </div>

              <div className="ant-form-item-label" style={{ marginBottom: "8px" }}>
                <label className="ant-form-item-required">
                  {t("category.fields.countries")}
                  <span className="text-red-500">*</span>
                </label>
              </div>
              <div style={{ position: "relative" }}>
                <CommonMultiSelect
                  options={countryOptions}
                  value={selectedCountries}
                  onChange={handleCountryChange}
                  onClose={() => {
                    if (!selectedCountries || selectedCountries.length === 0) {
                      setCountryError(true);
                    }
                  }}
                  placeholder={t("category.fields.countries")}
                  size="small"
                />
                {countryError && (
                  <div style={{ color: "#ff4d4f", fontSize: "14px", marginTop: "4px" }}>
                    {t("category.validation.country_required")}
                  </div>
                )}
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card title={t("category.sections.display_settings")} style={{ height: "100%" }}>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  px={2}
                  py={1}
                  sx={{ border: "1px solid var(--app-border-light)", borderRadius: "8px" }}
                >
                  <span style={{ fontWeight: 500 }}>{t("category.fields.color")}</span>
                  <Form.Item name={"color"} style={{ margin: 0 }}>
                    <input
                      type="color"
                      style={{
                        width: "80px",
                        height: "32px",
                        borderRadius: "5%",
                        cursor: "pointer",
                        padding: 1,
                        appearance: "none",
                        WebkitAppearance: "none",
                      }}
                    />
                  </Form.Item>
                </Box>
                {settingRow(t("category.fields.active"), "active")}
                {settingRow(t("category.fields.show_home_image"), "showHomeImage")}
              </Box>
            </Card>
          </Col>

          <Col span={24}>
            <Card title={t("category.sections.images")}>
              <Row gutter={[16, 0]}>
                {imageField(
                  "serviceImage",
                  t("category.fields.service_image"),
                  category?.service_image || category?.active_image || "",
                  setServiceImage
                )}
                {imageField(
                  "background",
                  t("category.fields.background"),
                  category?.background_image || "",
                  setBackground
                )}
                {imageField(
                  "homeImage",
                  t("category.fields.home_image"),
                  category?.home_image || "",
                  setHomeImage
                )}
              </Row>
            </Card>
          </Col>
        </Row>
        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          <Button style={{ width: "120px" }} onClick={() => navigate(-1)}>
            {t("btn.cancel")}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            style={{ width: "120px", backgroundColor: "#ba2156", border: "none" }}
            loading={loadingEdit || loadingSave}
            disabled={loadingEdit || loadingSave || countryError}
          >
            {loadingSave ? t("category.detail.saving") : t("btn.save")}
          </Button>
        </Box>
      </Form>
      <Box mt={2}>
        <CategoryChildren
          mutate={mutate}
          data={category?.children || []}
          loading={isLoading}
        />
      </Box>
      {/* Translation Modal */}
      <TranslationModal
        open={isTranslationModalVisible}
        onCancel={() => setIsTranslationModalVisible(false)}
        onSuccess={handleTranslationSuccess}
        entityId={id || ""}
        entityType="category"
        translations={translations}
        languages={languages || []}
        title={t("category.translations_title")}
        entityName={currentTranslation?.translation_name}
      />
    </Box>
  );
};

export default CategoryDetail;
